import { useEffect, useState } from 'react';
import { getClinicHours, type ClinicHours, type ClinicSession, type WeekDay } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// Loaded once per clinic and shared by the booking form and the calendar; Settings refreshes it after a save
let cached: { clinicId: string; hours: Promise<ClinicHours | null> } | null = null;
const listeners = new Set<(h: ClinicHours | null) => void>();

function load(clinicId: string) {
  if (cached?.clinicId !== clinicId) {
    const hours = getClinicHours().then(r => r?.hours ?? null).catch(() => { cached = null; return null; });
    cached = { clinicId, hours };
  }
  return cached.hours;
}

export function setClinicHoursCache(clinicId: string, hours: ClinicHours | null) {
  cached = { clinicId, hours: Promise.resolve(hours) };
  listeners.forEach(l => l(hours));
}

/** The clinic's weekly hours; null while loading or when the clinic hasn't set them */
export function useClinicHours() {
  const clinicId = useAuth().currentUser?.clinic_id;
  const [hours, setHours] = useState<ClinicHours | null>(null);
  useEffect(() => {
    if (!clinicId) return;
    let live = true;
    load(clinicId).then(h => { if (live) setHours(h); });
    listeners.add(setHours);
    return () => { live = false; listeners.delete(setHours); };
  }, [clinicId]);
  return hours;
}

export const WEEK_DAYS: WeekDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** The sessions on a date (YYYY-MM-DD or Date); undefined when the clinic hasn't set its hours */
export function sessionsOn(hours: ClinicHours | null, date: string | Date): ClinicSession[] | undefined {
  if (!hours) return undefined;
  const d = typeof date === 'string' ? new Date(`${date.slice(0, 10)}T00:00:00`) : date;
  return hours[WEEK_DAYS[(d.getDay() + 6) % 7]] ?? [];
}

/** "10 AM – 1 PM" */
export function sessionLabel(s: ClinicSession) {
  const t = (hm: string) => {
    const [h, m] = hm.split(':').map(Number);
    return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
  };
  return `${t(s.start)} – ${t(s.end)}`;
}
